import React, { useEffect, useState, useCallback, useContext } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Legend,
    LabelList,
} from "recharts";
import {
    fetchVideoMetricsData,
    fetchDailyVideoWatchDetail,
} from "../../Api/VideoMetrics";
import { UserAuthenticationContext } from "../../Context/UserAuthenticationContext";
import { format, parseISO } from "date-fns";

const TOP_COUNTRY_COLORS: string[] = [
    "#8884d8", // Blue
    "#ff7300", // Orange
    "#00c49f", // Green
    "#ffbb28", // Yellow
    "#ff8042", // Red
    "#6a0dad", // Purple
    "#20c997", // Teal
    "#ff1493", // Pink
];

const TotalViewsReport: React.FC = () => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [details, setDetails] = useState<any[]>([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const authCtx = useContext(UserAuthenticationContext);

    const access_token = authCtx.getAccessTokenSync() ?? "";
    const [countryKeys, setCountryKeys] = useState<string[]>([]);
    const [countryColors, setCountryColors] = useState<{ [key: string]: string }>({});

    const COUNTRY_NAMES: { [key: string]: string } = {
        AF: "Afghanistan", AL: "Albania", DZ: "Algeria", AD: "Andorra", AO: "Angola",
        AR: "Argentina", AM: "Armenia", AU: "Australia", AT: "Austria", AZ: "Azerbaijan",
        BH: "Bahrain", BD: "Bangladesh", BY: "Belarus", BE: "Belgium", BJ: "Benin",
        BO: "Bolivia", BA: "Bosnia and Herzegovina", BR: "Brazil", BG: "Bulgaria",
        CA: "Canada", CL: "Chile", CN: "China", CO: "Colombia", HR: "Croatia",
        CU: "Cuba", CY: "Cyprus", CZ: "Czech Republic", DK: "Denmark",
        DO: "Dominican Republic", EG: "Egypt", EE: "Estonia", FI: "Finland",
        FR: "France", GE: "Georgia", DE: "Germany", GR: "Greece", HK: "Hong Kong",
        HU: "Hungary", IS: "Iceland", IN: "India", ID: "Indonesia", IR: "Iran",
        IQ: "Iraq", IE: "Ireland", IL: "Israel", IT: "Italy", JM: "Jamaica",
        JP: "Japan", JO: "Jordan", KZ: "Kazakhstan", KE: "Kenya", KR: "South Korea",
        KW: "Kuwait", LV: "Latvia", LB: "Lebanon", LT: "Lithuania", LU: "Luxembourg",
        MY: "Malaysia", MX: "Mexico", MA: "Morocco", NP: "Nepal", NL: "Netherlands",
        NZ: "New Zealand", NI: "Nicaragua", NG: "Nigeria", NO: "Norway", OM: "Oman",
        PK: "Pakistan", PA: "Panama", PY: "Paraguay", PE: "Peru", PH: "Philippines",
        PL: "Poland", PT: "Portugal", QA: "Qatar", RO: "Romania", RU: "Russia",
        SA: "Saudi Arabia", RS: "Serbia", SG: "Singapore", SK: "Slovakia",
        SI: "Slovenia", ZA: "South Africa", ES: "Spain", LK: "Sri Lanka",
        SE: "Sweden", CH: "Switzerland", TW: "Taiwan", TH: "Thailand", TR: "Turkey",
        UA: "Ukraine", AE: "United Arab Emirates", GB: "United Kingdom",
        US: "United States", UY: "Uruguay", VE: "Venezuela", VN: "Vietnam",
        YE: "Yemen", ZW: "Zimbabwe",
    };

    useEffect(() => {
        const abortController = new AbortController();

        const fetchData = async () => {
            try {
                const response = await fetchVideoMetricsData(access_token, abortController);
                const countryTotals: { [key: string]: number } = {};
                const formattedData = response.map((entry: any) => {
                    let totalViews = 0;
                    Object.keys(entry).forEach((key) => {
                        if (key !== "Date") {
                            const v = entry[key] || 0;
                            totalViews += v;
                            countryTotals[key] = (countryTotals[key] || 0) + v;
                        }
                    });
                    return { ...entry, Date: entry.Date.split("T")[0], TotalViews: totalViews };
                });

                const sortedCountries = Object.entries(countryTotals)
                    .sort(([, a], [, b]) => b - a)
                    .map(([country]) => country);

                const dynamicCountryColors: { [key: string]: string } = {};
                sortedCountries.forEach((country, index) => {
                    dynamicCountryColors[country] =
                        index < TOP_COUNTRY_COLORS.length ? TOP_COUNTRY_COLORS[index] : "#999";
                });

                setCountryKeys(sortedCountries);
                setCountryColors(dynamicCountryColors);
                setData(formattedData);
            } catch {
                setError("Failed to load data");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        return () => abortController.abort();
    }, [access_token]);

    /** ✅ Click handler using Bar Data */
    const handleBarClick = useCallback(
        async (barData: any) => {
            if (!barData || !barData.Date) return;
            setSelectedDate(barData.Date);
            setLoadingDetails(true);
            const abortController = new AbortController();
            try {
                const detailData = await fetchDailyVideoWatchDetail(barData.Date, access_token, abortController);
                setDetails(detailData);
            } catch {
                setError("Failed to load details");
            } finally {
                setLoadingDetails(false);
            }
        },
        [access_token]
    );

    if (loading) return <p>Loading...</p>;
    if (error) return <p style={{ color: "red" }}>{error}</p>;

    return (
        <div
            style={{
                width: "100%",
                height: "auto",
                padding: "20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "10px",
            }}
        >
            <h2 style={{ textAlign: "center", color: "#333" }}>
                Top 8 Countries - Videos Watched (Last 7 Days)
            </h2>

            <ResponsiveContainer width="100%" height={450}>
                <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
                    <XAxis
                        dataKey="Date"
                        tick={{ fontSize: 12, fontWeight: "bold" }}
                        tickFormatter={(dateString) => {
                            try {
                                return format(parseISO(dateString), "dd-MM-yyyy");
                            } catch {
                                return dateString;
                            }
                        }}
                    />
                    <YAxis tick={{ fontSize: 12, fontWeight: "bold" }} />
                    <Tooltip
                        cursor={{ fill: "rgba(136,132,216,0.2)" }}
                        formatter={(value: number, name: string, item: any) => {
                            if (!item || !Array.isArray(item.payload)) {
                                return [`${value} Views`, COUNTRY_NAMES[name] || name];
                            }
                            const sortedEntries = item.payload
                                .map((entry: any) => ({
                                    country: COUNTRY_NAMES[entry.name as string] || entry.name,
                                    views: typeof entry.value === "number" ? entry.value : 0,
                                }))
                                .sort((a: any, b: any) => b.views - a.views);
                            return sortedEntries.map((entry: any) => [`${entry.views} Views`, entry.country]);
                        }}
                    />
                    <Legend verticalAlign="top" wrapperStyle={{ fontSize: "14px" }} />
                    <Bar dataKey="TotalViews" fill="transparent" stackId="total" pointerEvents="none">
                        <LabelList
                            dataKey="TotalViews"
                            position="top"
                            fill="#333"
                            fontSize={14}
                            fontWeight="bold"
                            formatter={(value: number) => `${value} Views`}
                        />
                    </Bar>
                    {countryKeys.map((country) => (
                        <Bar
                            key={country}
                            dataKey={country}
                            stackId="a"
                            fill={countryColors[country]}
                            name={COUNTRY_NAMES[country] || country}
                            cursor="pointer"
                            onClick={(event) => handleBarClick(event)}
                        >
                            <LabelList dataKey={country} position="inside" fill="#fff" fontSize={12} fontWeight="bold" />
                        </Bar>
                    ))}
                </BarChart>
            </ResponsiveContainer>

            {/* ✅ Detailed Section — DIV-BASED "TABLE" (immune to global table CSS) */}
            {selectedDate && (
                <div
                    style={{
                        marginTop: "20px",
                        padding: "20px",
                        backgroundColor: "#fff",
                        borderRadius: "10px",
                        boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
                        overflowX: "auto",
                        position: "relative",
                        zIndex: 10,
                    }}
                >
                    <h3 style={{ textAlign: "center", color: "#333" }}>
                        Detailed View for {new Date(selectedDate).toLocaleDateString("en-GB")}
                    </h3>

                    {loadingDetails ? (
                        <p style={{ textAlign: "center", fontStyle: "italic" }}>Loading details...</p>
                    ) : (
                        <div
                            role="table"
                            aria-label={`Video details for ${selectedDate}`}
                            style={{
                                width: "100%",
                                marginTop: "10px",
                                border: "1px solid #ddd",
                                borderRadius: "6px",
                                overflow: "hidden",
                                background: "#fff",
                                minWidth: "600px",
                            }}
                        >
                            {/* Header */}
                            <div
                                role="row"
                                aria-rowindex={1}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 140px",
                                    background: "#8884d8",
                                    color: "#fff",
                                    fontWeight: 600,
                                }}
                            >
                                <div role="columnheader" style={{ padding: "12px", borderBottom: "2px solid #ddd" }}>
                                    Video Title
                                </div>
                                <div role="columnheader" style={{ padding: "12px", borderBottom: "2px solid #ddd" }}>
                                    Views
                                </div>
                            </div>

                            {/* Body */}
                            <div role="rowgroup">
                                {details.map((detail, index) => {
                                    const bg = index % 2 === 0 ? "#ffffff" : "#f2f2f2"; // white / light grey
                                    return (
                                        <div
                                            key={index}
                                            role="row"
                                            aria-rowindex={index + 2}
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "1fr 140px",
                                                background: bg,
                                            }}
                                        >
                                            <div
                                                role="cell"
                                                style={{
                                                    padding: "12px",
                                                    borderBottom: "1px solid #ddd",
                                                    color: "#333",
                                                }}
                                            >
                                                {detail.Title}
                                            </div>
                                            <div
                                                role="cell"
                                                style={{
                                                    padding: "12px",
                                                    borderBottom: "1px solid #ddd",
                                                    color: "#333",
                                                }}
                                            >
                                                {detail.Qty}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TotalViewsReport;
